import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, ScrollView, View } from 'react-native';
import TabStrip from './TabStrip';
import styles from './ItineraryEditor.styles';

type ItineraryTabsProps = {
  days: any[];
  selectedIndex: number;
  onSelect: (i: number) => void;
  isEditing?: boolean;
  onAddDay?: () => void;
};

const ItineraryTabs: React.FC<ItineraryTabsProps> = ({ days, selectedIndex, onSelect, isEditing = false, onAddDay }) => {
  const [tabLayouts, setTabLayouts] = useState<Record<number, { x: number; width: number }>>({});
  const indicatorX = useRef(new Animated.Value(0)).current;
  const indicatorWidth = useRef(new Animated.Value(0)).current;
  const tabsScrollRef = useRef<ScrollView | null>(null);
  const scrollX = useRef(new Animated.Value(0)).current;
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const handleScroll = useRef(
    Animated.event([{ nativeEvent: { contentOffset: { x: scrollX } } }], {
      useNativeDriver: false,
    }),
  ).current;

  useEffect(() => {
    const idx = selectedIndex || 0;
    const layout = tabLayouts[idx];
    if (!layout) return;
    Animated.parallel([
      Animated.timing(indicatorX, { toValue: layout.x, duration: 220, useNativeDriver: false }),
      Animated.timing(indicatorWidth, { toValue: layout.width, duration: 220, useNativeDriver: false }),
    ]).start();
    try {
      const scrollRef = tabsScrollRef.current as any;
      if (scrollRef && scrollRef.scrollTo && containerWidth > 0) {
        const effectiveWidth = containerWidth;
        const maxOffset = Math.max(0, (contentWidth || 0) - effectiveWidth);
        const desired = layout.x + layout.width / 2 - effectiveWidth / 2;
        const target = Math.min(Math.max(0, desired), maxOffset);
        if (Number.isFinite(target)) {
          scrollRef.scrollTo({ x: target, animated: true });
        }
      }
    } catch (e) {}
  }, [selectedIndex, tabLayouts, indicatorX, indicatorWidth, containerWidth, contentWidth]);

  const indicatorTranslateX = useMemo(() => Animated.subtract(indicatorX, scrollX), [indicatorX, scrollX]);

  return (
    <View style={{ position: 'relative' }}>
      <TabStrip
        days={days}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        onTabLayout={(i, x, width) => setTabLayouts((prev) => ({ ...prev, [i]: { x, width } }))}
        isEditing={isEditing}
        onAddDay={onAddDay}
        scrollRef={tabsScrollRef}
        onScroll={handleScroll}
        onContainerLayout={(width) => {
          if (typeof width === 'number' && width > 0 && width !== containerWidth) {
            setContainerWidth(width);
          }
        }}
        onContentSizeChange={(width) => {
          if (typeof width === 'number' && width > 0 && width !== contentWidth) {
            setContentWidth(width);
          }
        }}
      />
      <Animated.View
        style={[styles.tabIndicator, { pointerEvents: 'none', width: indicatorWidth, transform: [{ translateX: indicatorTranslateX }] }]}
      />
    </View>
  );
};

export default ItineraryTabs;
